import { z } from "zod";
import { BillingSettingsSchema } from "./billing.js";

/** Sunucuda saklanan sağlayıcı ayarları (v1.0 yapısı; passwordHash ve usageLog kalktı). */
export const ProviderFeaturesSchema = z.object({
  docx: z.boolean(), dxf: z.boolean(), polygonMode: z.boolean(), multiLayer: z.boolean(),
  lineLoads: z.boolean(), jointChecks: z.boolean(), classicTable: z.boolean(),
});
export const ProviderBrandingSchema = z.object({
  footerText: z.string().max(300),
  logoDataUrl: z.string().max(400_000).optional(),
  softwareNote: z.string().max(500),
  watermark: z.string().max(100).optional(),
});
export const ProviderDefaultsSchema = z.object({
  material: z.object({ gamma: z.number(), allowableCompression: z.number(), jointFriction: z.number(), jointCohesion: z.number() }).partial().optional(),
  seismic: z.object({
    r: z.union([z.literal(2.0), z.literal(1.5), z.literal(1.0)]),
    level: z.enum(["DD-1", "DD-2", "DD-3", "DD-4"]),
    includeWallInertia: z.boolean(),
    /** topuk/kama üstü dolgu + sürşarj ataleti (yalnız duvar ataleti açıkken etkili) */
    includeBackfillInertia: z.boolean(),
    kvOnTotalPressure: z.boolean(),
    dynamicPointOfApplication: z.enum(["H/2", "0.6H"]),
  }).partial().optional(),
  classic: z.record(z.string(), z.object({ static: z.number(), seismic: z.number() })).optional(),
  gammaRdev: z.number().optional(),
  gammaRdevSeismic: z.number().optional(),
  bearingRatio: z.number().optional(),
  passiveNeglectedDepth: z.number().optional(),
  /** Metraj poz / birim fiyat tablosu (yeni projelere kopyalanır) */
  pricing: z.object({ currency: z.string().max(8), year: z.string().max(40), items: z.array(z.object({ key: z.string().max(32), code: z.string().max(32), label: z.string().max(200), unitPrice: z.number().nonnegative() })).max(40) }).optional(),
});

/** Satıcı kimliği — Mesafeli Sözleşmeler Yönetmeliği m.5 ön bilgilendirme (herkese açık; ön bilgilendirme ve sözleşme sayfalarında basılır). */
export const SellerSchema = z.object({
  name: z.string().max(200).default(""),          // ticaret unvanı / ad soyad
  address: z.string().max(400).default(""),
  taxOffice: z.string().max(100).default(""),
  taxNo: z.string().max(40).default(""),          // vergi no / TCKN
  mersis: z.string().max(40).default(""),
  email: z.string().max(120).default(""),
  phone: z.string().max(60).default(""),
  kep: z.string().max(120).default(""),
});
export type Seller = z.infer<typeof SellerSchema>;

/** Bulutta kullanıcı başına saklanan proje sınırı (Cem 2026-09-29: varsayılan 10; dolunca "önce dışa aktarıp silin"). Yönetici sınırsız. */
export const ProjectLimitsSchema = z.object({
  free: z.number().int().min(1).max(10_000).default(2),
  paid: z.number().int().min(1).max(10_000).default(10),
});
export type ProjectLimits = z.infer<typeof ProjectLimitsSchema>;

export const ServerProviderSettingsSchema = z.object({
  version: z.literal(1).default(1),
  imagePdf: z.boolean().default(false),
  branding: ProviderBrandingSchema.default({
    footerText: "TaşDuvar — taş istinat duvarı analizi",
    softwareNote: "Bu rapor TaşDuvar yazılımı ile TBDY 2018 §16.12 esaslarına göre hazırlanmıştır.",
    watermark: "",
  }),
  defaults: ProviderDefaultsSchema.default({}),
  features: ProviderFeaturesSchema.default({ docx: true, dxf: true, polygonMode: true, multiLayer: true, lineLoads: true, jointChecks: true, classicTable: true }),
  billing: BillingSettingsSchema.default({}),
  seller: SellerSchema.default({}),
  limits: ProjectLimitsSchema.default({}),
});
export type ServerProviderSettings = z.infer<typeof ServerProviderSettingsSchema>;

/** Oturumsuz herkese görünen kesit (IBAN vb. yok). */
export const PublicProviderSchema = ServerProviderSettingsSchema.pick({ imagePdf: true, branding: true, defaults: true, features: true, seller: true });
export type PublicProvider = z.infer<typeof PublicProviderSchema>;

export const ExportFormatSchema = z.enum(["pdf", "pdf-image", "docx", "dxf", "json", "xlsx", "csv"]);
export const UsageBodySchema = z.object({
  projectName: z.string().max(200),
  wallCount: z.number().int().min(0),
  format: ExportFormatSchema,
  projectId: z.string().max(100).optional(),
});
export type UsageBody = z.infer<typeof UsageBodySchema>;

export const AdminUserDtoSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string(),
  role: z.enum(["user", "admin"]),
  emailVerified: z.boolean(),
  blocked: z.boolean(),
  createdAt: z.string(),
  projectCount: z.number().int(),
  subscription: z.object({ status: z.string(), planCode: z.string().nullable(), endsAt: z.string().nullable() }),
});
export type AdminUserDto = z.infer<typeof AdminUserDtoSchema>;

export const AdminPatchUserSchema = z.object({
  role: z.enum(["user", "admin"]).optional(),
  blocked: z.boolean().optional(),
  subscription: z.object({ planCode: z.string(), endsAt: z.string().datetime({ offset: true }).nullable().optional() }).optional(),
});

export const AdminStatsSchema = z.object({
  users: z.number().int(),
  activeSubs: z.number().int(),
  trialing: z.number().int(),
  projects: z.number().int(),
  reports30d: z.number().int(),
  pendingPayments: z.number().int(),
});
export type AdminStats = z.infer<typeof AdminStatsSchema>;
